/**
 * El nombre de su columna en cada celda, para las tarjetas del móvil · 13-10-2026
 *
 * En el móvil las tablas son tarjetas (`tema.css`, `.tabla`), y cada línea de la
 * tarjeta lleva delante el nombre de su columna, que el CSS lee de `data-label`.
 * Ponerlo a mano en las cerca de cincuenta tablas del panel era una tarea que se
 * olvida en la siguiente: esto lo copia de la cabecera de la tabla, sola y para
 * siempre, también en las que se escriban mañana.
 *
 * Las reglas, pocas:
 *
 * - La celda que ya lo trae (`data-label` en el JSX) se respeta.
 * - La primera columna es el título de la tarjeta y no lleva nombre, salvo que la
 *   fila marque otra como título (`celda-titulo`, la de «Recibos»).
 * - Una columna sin título —la de los botones— tampoco: va a lo ancho.
 *
 * Es un `MutationObserver` sobre el documento, con lo que se junta en un fotograma:
 * escribir un atributo no es un cambio de los que vigila, así que no se realimenta.
 */

const SELECTOR = 'table.tabla:not(.tabla-fija)';

/** Una pasada sobre lo que haya. Exportada para las pruebas. */
export function etiquetar(raiz: ParentNode): void {
  for (const tabla of raiz.querySelectorAll<HTMLTableElement>(SELECTOR)) {
    const cabeceras = tabla.querySelectorAll<HTMLTableCellElement>(
      'thead tr:last-child > th'
    );
    if (cabeceras.length === 0) continue;

    const nombres: string[] = [];
    for (const th of cabeceras) {
      const texto = (th.textContent ?? '').trim();
      for (let i = 0; i < (th.colSpan || 1); i += 1) nombres.push(texto);
    }

    for (const fila of tabla.querySelectorAll<HTMLTableRowElement>(
      ':scope > tbody > tr, :scope > tfoot > tr'
    )) {
      const conTitulo = fila.querySelector(':scope > .celda-titulo') !== null;
      let columna = 0;
      for (const celda of fila.cells) {
        const aqui = columna;
        columna += celda.colSpan || 1;
        if (celda.hasAttribute('data-label')) continue;
        if (celda.classList.contains('celda-titulo')) continue;
        if (aqui === 0 && !conTitulo) continue;
        const nombre = nombres[aqui];
        if (nombre) celda.setAttribute('data-label', nombre);
      }
    }
  }
}

/** Empieza a vigilar. Devuelve cómo dejar de hacerlo. */
export function vigilarTablas(raiz: HTMLElement = document.body): () => void {
  let fotograma = 0;
  const programar = () => {
    if (fotograma) return;
    fotograma = requestAnimationFrame(() => {
      fotograma = 0;
      etiquetar(raiz);
    });
  };

  const observador = new MutationObserver(programar);
  observador.observe(raiz, { childList: true, subtree: true });
  programar();

  return () => {
    observador.disconnect();
    if (fotograma) cancelAnimationFrame(fotograma);
  };
}
